import {
  createPurposeModel,
  Purpose,
} from "@/shareholderRegister/transactions/commonCapitalReduction";
import { TransactionType } from "@/types/enums/TransactionType";
import {
  NumberOfShares,
  TransactionDateCardModel,
} from "@/shareholderRegister/transactions/commonTransactionsDomain";
import { makeAutoObservable } from "mobx";
import {
  ReductionOfNominalValue,
  ReductionOfNominalValueDTO,
  Transaction,
} from "@/types/Transactions";
import Big from "big.js";
import { combineDateAndTime } from "@/utils/Dates";
import { TransactionStore } from "@/stores/TransactionStore";
import {
  BooleanFieldModel,
  LocalDateFieldModel,
  NumberFieldModel,
  RadioFieldModel,
} from "@/common/commonForms";
import { Amount, isPending } from "@/common/common";
import { ShareType, ShareTypeCode } from "@/common/share";

export type OverviewForSharetype = {
  numberOfShares: NumberOfShares;
  shareType: ShareType;
  nominalValueBefore: number;
  nominalValueAfter: number;
  shareCapitalBefore: number;
  shareCapitalAfter: number;
  reductionInShareCapital: number;
};

export class ReductionOfNominalValueTransactionModel {
  purposeModel: RadioFieldModel<Purpose | undefined>;
  reduceInNominalValuePerShare: NumberFieldModel;
  transactionDateCardModel: TransactionDateCardModel;
  resolutionDate: LocalDateFieldModel;
  isDirty = false;
  excessCapital: BooleanFieldModel;
  excessCapitalModel: ExcessAmountModel[] = [];
  repaymentOfPaidInCapital: RadioFieldModel<boolean | undefined>;

  get nominalValueBefore(): number {
    return this.transactionStore.shareNominalValue ?? 0;
  }

  constructor(
    readonly transactionStore: TransactionStore,
    readonly companyData: { orgNumber: string; currency: string },
    readonly shareholderData: {
      shareTypes: ShareType[];
      shareSumBySharetypeCode: { [shareTypeCode in ShareTypeCode]?: number };
      transactionLog: Transaction[];
      loadShareholdersForDate: (orgNumber: string, date: string) => unknown;
    },
    readonly transaction?: Transaction,
  ) {
    const transactionData = transaction?.payload as ReductionOfNominalValueDTO | undefined;
    const excessMap = transactionData?.amountToBePaidInExcessOfReductionShareCapital ?? null;

    this.purposeModel = createPurposeModel({
      transactionType: TransactionType.REDUCTION_NOMINAL_VALUE,
      onChange: this.setDirty,
      value: transactionData?.shareReductionPurpose as Purpose | undefined,
    });
    this.reduceInNominalValuePerShare = new NumberFieldModel({
      isPositive: true,
      required: true,
      value: transactionData ? parseFloat(transactionData.reduceInNominalValuePerShare) : undefined,
      validator: (value) => {
        return value !== undefined && this.nominalValueBefore && value >= this.nominalValueBefore
          ? {
              key: "transactions.capitalReduction.totalReduction.reduction.negative",
            }
          : undefined;
      },
      onChange: this.setDirty,
    });
    this.resolutionDate = new LocalDateFieldModel({
      value: transactionData?.resolutionDate?.split("T")[0],
      onChange: this.setDirty,
    });
    this.transactionDateCardModel = new TransactionDateCardModel({
      data: this.shareholderData,
      value: transactionData?.registrationInNorwegianRegisterDate ?? undefined,
      onChange: (date, time) => {
        this.setDirty();
        if (date && time) {
          this.fetchState(combineDateAndTime(date, time));
        }
      },
    });

    this.excessCapital = new BooleanFieldModel({
      required: true,
      value: transactionData ? excessMap !== null && Object.keys(excessMap).length > 0 : undefined,
      onChange: this.setDirty,
    });
    this.excessCapitalModel = this.shareTypes.map(
      (shareType) =>
        new ExcessAmountModel({
          shareTypeCode: shareType.shareTypeCode,
          currency: this.companyData.currency,
          shareType: shareType,
          onChange: this.setDirty,
          value: excessMap?.[String(shareType.sharetypeId)]?.value,
        }),
    );
    this.repaymentOfPaidInCapital = new RadioFieldModel<boolean | undefined>({
      required: true,
      value: transactionData?.isTreatedAsRepayment ?? false,
      options: [false, true],
      idFn: (v) => (v === undefined ? "" : String(v)),
      labelFn: (v) =>
        v === false
          ? { key: "transactions.capitalReduction.treatAsRepayment.dividend" }
          : { key: "transactions.capitalReduction.treatAsRepayment.repaymentOfSharePremium" },
      onChange: this.setDirty,
    });

    makeAutoObservable(
      this,
      { transactionStore: false, companyData: false, shareholderData: false },
      { autoBind: true },
    );
  }

  get shareTypes() {
    return filterShareTypesWithShares(
      this.shareholderData.shareTypes,
      this.shareholderData.shareSumBySharetypeCode,
    );
  }

  get currency() {
    return this.companyData.currency;
  }

  *fetchState(date: string) {
    yield this.shareholderData.loadShareholdersForDate(this.companyData.orgNumber, date);
    yield this.transactionStore.fetchInitialTransactionState(this.companyData.orgNumber, date);
    this.rebuildExcessCapitalModels();
  }

  private rebuildExcessCapitalModels() {
    const transactionData = this.transaction?.payload as ReductionOfNominalValueDTO | undefined;
    const excessMap = transactionData?.amountToBePaidInExcessOfReductionShareCapital ?? null;
    this.excessCapitalModel = this.shareTypes.map(
      (shareType) =>
        new ExcessAmountModel({
          shareTypeCode: shareType.shareTypeCode,
          currency: this.companyData.currency,
          shareType: shareType,
          onChange: this.setDirty,
          value: excessMap?.[String(shareType.sharetypeId)]?.value,
        }),
    );
  }

  get isFetching() {
    return isPending(this.transactionStore.fetchInitialStateResult);
  }

  get overviewForSharetypes(): OverviewForSharetype[] {
    return Object.entries(this.shareholderData.shareSumBySharetypeCode).map<OverviewForSharetype>(
      ([sharetypeCode, numberOfShares]) => {
        const shareCapitalBefore = Big(numberOfShares).times(this.nominalValueBefore).toNumber();
        const nominalValueAfter = Big(this.nominalValueBefore)
          .minus(this.reduceInNominalValuePerShare.toNumberOrZero())
          .toNumber();
        const shareCapitalAfter = Big(numberOfShares).times(nominalValueAfter).toNumber();
        return {
          numberOfShares: {
            sharetypeCode: sharetypeCode as ShareTypeCode,
            numberOfShares: numberOfShares,
          },
          shareType: this.shareTypes.find((s) => s.shareTypeCode === sharetypeCode)!,
          nominalValueBefore: this.nominalValueBefore,
          nominalValueAfter: nominalValueAfter,
          shareCapitalBefore: shareCapitalBefore,
          shareCapitalAfter: shareCapitalAfter,
          reductionInShareCapital: Big(shareCapitalBefore).minus(shareCapitalAfter).toNumber(),
        };
      },
    );
  }

  get reductionInShareCapital(): number {
    return this.overviewForSharetypes
      .map((o) => Big(o.shareCapitalBefore).minus(o.shareCapitalAfter).toNumber())
      .reduce((agg, cur) => Big(agg).plus(cur).toNumber(), 0);
  }

  clearDirty() {
    this.isDirty = false;
  }

  setDirty() {
    this.isDirty = true;
  }

  get isValid(): boolean {
    if (!this.purposeModel.isValid) {
      return false;
    }
    if (!this.reduceInNominalValuePerShare.isValid) {
      return false;
    }
    if (this.overviewForSharetypes.every((o) => o.nominalValueAfter < 0)) {
      return !this.reduceInNominalValuePerShare.isValid;
    }
    if (
      this.purposeModel.value === Purpose.DISTRIBUTION &&
      this.excessCapital.value === true &&
      this.excessCapitalModel.some((e) => !e.excessAmount.isValid)
    ) {
      return false;
    }
    return this.transactionDateCardModel.isValid && this.resolutionDate.isValid;
  }

  validate(): boolean {
    this.purposeModel.validate();
    this.reduceInNominalValuePerShare.validate();
    this.transactionDateCardModel.validate();
    this.resolutionDate.validate();
    this.excessCapitalModel.forEach((e) => e.excessAmount.validate());
    return this.isValid;
  }

  prepareData(): ReductionOfNominalValue {
    const entries = new Map<number, Amount>();
    this.excessCapitalModel.forEach((amount) => {
      if (
        amount.sharetypeId &&
        amount.excessAmount.toNumberOrZero() >= 0 &&
        amount.excessAmount.inputValue !== ""
      )
        entries.set(amount.sharetypeId, {
          currency: amount.currency,
          value: amount.excessAmount.toNumberOrZero(),
        });
    });
    return {
      organizationnumber: this.companyData.orgNumber,
      shareReductionPurpose: this.purposeModel.value!,
      reduceInNominalValuePerShare: String(this.reduceInNominalValuePerShare.parsedValue),
      amountToBePaidInExcessOfReductionShareCapital: this.excessCapital.value
        ? Object.fromEntries(entries)
        : null,
      isTreatedAsRepayment: this.repaymentOfPaidInCapital.value === true,
      resolutionDate: this.resolutionDate.validatedValue
        ? combineDateAndTime(this.resolutionDate.validatedValue, "12:00")
        : undefined,
      isRegisteredInNorwegianBusinessEnterprise: true,
      registrationInNorwegianRegisterDate: this.transactionDateCardModel.combinedDateAndTime,
    };
  }

  submit() {
    this.transactionStore.reduceNominalValue(this, this.transaction?.transactionlogId);
  }

  get isSubmitting() {
    return isPending(this.transactionStore.result);
  }

  get isEdit() {
    return this.transaction !== undefined;
  }
}

export class ExcessAmountModel {
  readonly sharetypeCode: ShareTypeCode;
  readonly currency: string;
  readonly shareType: ShareType;
  sharetypeId: number;
  excessAmount: NumberFieldModel;

  constructor({
    shareTypeCode,
    currency,
    shareType,
    onChange,
    value,
  }: {
    shareTypeCode: ShareTypeCode;
    currency: string;
    shareType: ShareType;
    onChange: () => void;
    value?: number;
  }) {
    makeAutoObservable(this);
    this.sharetypeCode = shareTypeCode;
    this.currency = currency;
    this.shareType = shareType;
    this.sharetypeId = shareType.sharetypeId;
    this.excessAmount = new NumberFieldModel({
      isPositive: true,
      required: true,
      value,
      onChange,
    });
  }

  get isValid(): boolean {
    return this.excessAmount.isValid;
  }

  validate(): boolean {
    this.excessAmount.validate();
    return this.isValid;
  }
}

export function filterShareTypesWithShares(
  sharetypes: ShareType[],
  shareSumBySharetypeCode: { [shareTypeCode in ShareTypeCode]?: number },
): ShareType[] {
  return sharetypes.filter(
    (sharetype) => (shareSumBySharetypeCode[sharetype.shareTypeCode] ?? 0) > 0,
  );
}
